import { ApiController } from '@/services/ApiController';
import { ApiRoutes } from '@/enums/api/ApiRoutes';
import { AxiosResponse } from 'axios';

export function withUserGroup(url: string, userGroupId: number): string {
    return `${url}${url.includes('?') ? '&' : '?'}__ug=${Number(userGroupId)}`;
}

export interface InternalOrderPrefill {
    documentType: string | null;
    technologistName: string | null;
    canCreate: boolean;
    userGroupId: number | null;
}

export default class InternalOrderWithoutConfigurationAPI {
    public static async searchClients(query: string, userGroupId: number): Promise<any[]> {
        const trimmed = query.trim();
        let routeQuery = '';

        if (trimmed) {
            const encodedQuery = encodeURIComponent(`%${trimmed}%`);
            routeQuery += `&q=(name:${encodedQuery}|address.streetNameAndNumber:${encodedQuery}|address.city:${encodedQuery}|address.poNumber:${encodedQuery}|contactPersons.phone:${encodedQuery})`;
        }

        routeQuery += '&sort=name&page[size]=15';

        const response = (await ApiController.get(
            withUserGroup(ApiRoutes.client.search(routeQuery), userGroupId),
        )) as AxiosResponse;

        return Array.isArray(response.data) ? response.data : [];
    }

    public static async getSalesmen(userGroupId: number): Promise<any[]> {
        const response = (await ApiController.get(
            withUserGroup(ApiRoutes.cmsUser.getAllOnlyUsers, userGroupId),
        )) as AxiosResponse;
        const users = Array.isArray(response.data) ? response.data : [];

        return users.filter((user: any) => user.isSalesman && !user.isArchived);
    }

    public static async getPrefill(clientId: string, userGroupId: number): Promise<InternalOrderPrefill> {
        const response = (await ApiController.get(
            withUserGroup(`/technologist/orders/prefill-client/${clientId}`, userGroupId),
        )) as AxiosResponse;

        return response.data as InternalOrderPrefill;
    }

    public static async createProject(
        data: {
            clientId: string;
            contactPersonId: string;
            assignedUserId?: string | null;
            madeByUserId?: string | null;
            connection?: string | null;
        },
        userGroupId: number,
    ): Promise<AxiosResponse> {
        const relationships: any = {
            client: { data: { type: 'clients', id: data.clientId } },
            contactPerson: { data: { type: 'contact_persons', id: data.contactPersonId } },
        };

        if (data.assignedUserId) {
            relationships.assignedUser = { data: { type: 'users', id: data.assignedUserId } };
        }

        if (data.madeByUserId) {
            relationships.madeByUser = { data: { type: 'users', id: data.madeByUserId } };
        }

        const attributes: any = {};

        if (data.connection) {
            attributes.connection = data.connection;
        }

        const payload = { data: { type: 'projects', attributes, relationships } };

        return (await ApiController.post(withUserGroup(ApiRoutes.createNewProject, userGroupId), payload)) as AxiosResponse;
    }

    public static async createOffer(
        projectId: string,
        paymentTypeId: string | null,
        userGroupId: number,
    ): Promise<AxiosResponse> {
        const relationships: any = {
            project: { data: { type: 'projects', id: projectId } },
        };

        if (paymentTypeId) {
            relationships.paymentType = { data: { type: 'payment_types', id: paymentTypeId } };
        }

        const payload = { data: { type: 'offers', attributes: {}, relationships } };

        return (await ApiController.post(withUserGroup(ApiRoutes.offer.create, userGroupId), payload)) as AxiosResponse;
    }

    public static async createTechnologistOrder(
        technologistOrderData: { [key: string]: any },
        offerId: string,
        userGroupId: number,
    ): Promise<AxiosResponse> {
        const payload = {
            data: {
                type: 'technologist_orders',
                attributes: { ...technologistOrderData },
                relationships: {
                    offer: { data: { type: 'offers', id: offerId } },
                },
            },
        };

        return (await ApiController.post(
            withUserGroup(ApiRoutes.technologist.create, userGroupId),
            payload,
        )) as AxiosResponse;
    }

    public static async uploadTechnologistAttachment(
        technologistOrderId: string,
        file: Blob,
        documentType: string | null,
        userGroupId: number,
    ): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('technologist_order_id', technologistOrderId);

        if (documentType) {
            formData.append('document_type', documentType);
        }

        return (await ApiController.post(
            withUserGroup(ApiRoutes.technologistOrderAttachment.create, userGroupId),
            formData,
        )) as AxiosResponse;
    }

    public static async createInternalOrder(
        data: { system: string; quantity: number; projectId: string; offerId: string },
        userGroupId: number,
    ): Promise<AxiosResponse> {
        const payload = {
            data: {
                type: 'project_internal_order',
                attributes: {
                    system: data.system,
                    quantity: data.quantity,
                    // orderNumber: data.offerId,
                    type: 'order',
                },
                relationships: {
                    project: { data: { type: 'projects', id: data.projectId } },
                },
            },
        };

        return (await ApiController.post(
            withUserGroup(ApiRoutes.internalOrder.create, userGroupId),
            payload,
        )) as AxiosResponse;
    }

    public static async updateInternalOrderColor(
        internalOrderId: string,
        colorData: {
            colorName: string | null;
            colorHex: string | null;
            powderId?: string | null;
            powderName: string | null;
            ingeniousColor: string | null;
        },
        userGroupId: number,
    ): Promise<AxiosResponse> {
        const payload = {
            data: {
                type: 'project_internal_order',
                id: internalOrderId,
                attributes: { internalOrderColorData: colorData },
            },
        };

        return (await ApiController.patch(
            withUserGroup(ApiRoutes.internalOrder.update(internalOrderId), userGroupId),
            payload,
        )) as AxiosResponse;
    }

    public static async uploadInternalOrderAttachment(
        internalOrderId: string,
        file: Blob,
        documentType: string | null,
        userGroupId: number,
    ): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('project_internal_order_id', internalOrderId);

        if (documentType) {
            formData.append('document_type', documentType);
        }

        return (await ApiController.post(
            withUserGroup(ApiRoutes.projectInternalOrderAttachment.create, userGroupId),
            formData,
        )) as AxiosResponse;
    }

    public static async orderByTechnologist(offerId: string, userGroupId: number): Promise<AxiosResponse> {
        const payload = { data: { type: 'offers', attributes: {}, relationships: {} } };

        return (await ApiController.post(
            withUserGroup(ApiRoutes.offer.orderByTechnologist(offerId), userGroupId),
            payload,
        )) as AxiosResponse;
    }
}